import { Dispatch, SetStateAction, useMemo } from 'react';

import {
  ChakraFlex,
  FAIconName,
  FireflyButton,
  TableFilter,
  TableFilterElement,
} from '@equip.health/ui';

import { getInitialFilterValue } from '~/lib/util/patient.util';

const TOP_LEVEL_ID = 'patient-list-filters';

const filterConfig: {
  key: keyof PatientListFiltersType;
  title: string;
  isNullOnRemove?: boolean;
  isBooleanFilter?: boolean;
}[] = [
  { key: 'name', title: 'Name', isNullOnRemove: true },
  { key: 'primaryDiagnosis', title: 'Primary diagnosis' },
  { key: 'episodeLevels', title: 'Level' },
  { key: 'psychClearanceStatuses', title: 'Psych clearance status' },
  { key: 'episodeStatusReasons', title: 'Closed reason' },
  { key: 'treatmentPrograms', title: 'Treatment program' },
  { key: 'insuranceVerificationStatus', title: 'Insurance' },
  { key: 'medicalClearanceStatus', title: 'Medical clearance' },
  { key: 'admitDateFrom', title: 'Admit date from', isNullOnRemove: true },
  { key: 'admitDateTo', title: 'Admit date to', isNullOnRemove: true },
  { key: 'careTeamMember', title: 'Provider team member' },
  { key: 'payor', title: 'Payor' },
  { key: 'payorProgram', title: 'Payor program' },
  { key: 'isPatientPaused', title: 'Patient is paused', isBooleanFilter: true },
  { key: 'universalStatuses', title: 'Patient status' },
];

const getFilterLabel = (
  value: string | PatientFilterType | boolean,
): string => {
  if (typeof value === 'boolean') return value ? 'Yes' : '';
  if (typeof value === 'string') return value;
  return value?.displayText ?? '';
};

interface PatientFilterParamsProps {
  filter: PatientListFiltersType;
  setFilter: Dispatch<SetStateAction<PatientListFiltersType>>;
}

const PatientFilterParams = ({
  filter,
  setFilter,
}: PatientFilterParamsProps) => {
  const initialFilter = useMemo(() => getInitialFilterValue(undefined), []);

  const activeFilters = useMemo<TableFilter[]>(() => {
    const result: TableFilter[] = [];

    for (const config of filterConfig) {
      const value = filter[config.key];

      if (config.isBooleanFilter) {
        if (value) {
          result.push({
            filterKey: config.key,
            title: config.title,
            filters: [{ value: 'true', label: 'Yes' }],
          });
        }
        continue;
      }

      if (Array.isArray(value)) {
        const labels = value
          .map(getFilterLabel)
          .filter((label) => label !== '');
        if (labels.length > 0) {
          result.push({
            filterKey: config.key,
            title: config.title,
            filters: labels.map((label) => ({ value: label, label })),
          });
        }
        continue;
      }

      const label = getFilterLabel(value as string | PatientFilterType);
      if (label) {
        result.push({
          filterKey: config.key,
          title: config.title,
          filters: [{ value: label, label }],
        });
      }
    }

    return result;
  }, [filter]);

  const handleRemoveFilter = (filterKey: string) => {
    setFilter((prev) => ({
      ...prev,
      [filterKey]:
        initialFilter[filterKey as keyof PatientListFiltersType],
    }));
  };

  const handleResetAll = () => {
    setFilter(getInitialFilterValue(undefined));
  };

  return (
    <ChakraFlex alignItems="center" gap="12px">
      <ChakraFlex alignItems="center" flexWrap="wrap" gap="8px">
        {activeFilters.map((tableFilter) => (
          <TableFilterElement
            filter={tableFilter}
            key={tableFilter.filterKey}
            onRemove={handleRemoveFilter}
          />
        ))}
      </ChakraFlex>
      <FireflyButton
        id={`${TOP_LEVEL_ID}__reset`}
        leftIcon={FAIconName.redoRegular}
        onClick={handleResetAll}
        size="small"
        variant="secondary"
      >
        Reset filters
      </FireflyButton>
    </ChakraFlex>
  );
};

export default PatientFilterParams;
